import axios from 'axios';

/**
 * Collect every leaf string out of a DRF error value.
 *
 * A field's errors arrive as a list of strings, but nested serializers nest
 * further lists and objects — flattening avoids rendering "[object Object]".
 */
const flattenMessages = (value: unknown): string[] => {
  if (value === null || value === undefined) return [];
  if (Array.isArray(value)) return value.flatMap(flattenMessages);
  if (typeof value === 'object') {
    return Object.values(value as Record<string, unknown>).flatMap(flattenMessages);
  }
  const message = String(value).trim();
  return message ? [message] : [];
};

/**
 * Flatten one field's backend errors into a single string for a form field.
 *
 * DRF's own messages end in a full stop ("This field is required."), but custom
 * validators frequently don't, so each message is given terminal punctuation
 * before joining — otherwise two messages run together as one sentence.
 *
 * @example joinFieldErrors(['This field is required.', 'Must be positive'])
 *          // 'This field is required. Must be positive.'
 */
export const joinFieldErrors = (value: unknown): string =>
  flattenMessages(value)
    .map(message => (/[.!?]$/.test(message) ? message : `${message}.`))
    .join(' ');

/**
 * A toast-ready message for a failed request.
 *
 * Tells "the server answered with an error" apart from "the request never got an
 * answer" — reporting a server-side 500 as a network failure sends people to check
 * their connection instead of the logs.
 */
export const describeRequestError = (error: unknown, action: string): string => {
  if (!axios.isAxiosError(error) || !error.response) {
    return `Could not reach the server to ${action}. Please try again.`;
  }
  const detail = error.response.data?.detail;
  return typeof detail === 'string' && detail
    ? detail
    : `Could not ${action} (server error ${error.response.status}).`;
};
